import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import JourneyStepProp from '../../propTypes/journeyStepProp';
import translate from '../../../../commonComponents/translate';
import {
  compose,
  handleUploadFile,
  JOURNEY_STEP_TYPE,
  resolveDocumentUrl,
} from '../../../../services/utilityFunctions';
import QuillPdf from '../../../../commonComponents/fileViewers/QuillPdf';
import QuillSigner from '../../../../commonComponents/fileViewers/QuillSigner';
import Notifications from '../../../../services/notifications';
import withCSRF from '../../../../commonComponents/withCSRF';
import FileUploadBtn from '../../../../commonComponents/forms/FileUploadBtn';
import PdfInfoSection from './components/PdfInfoSection';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import { fillFormFields } from '../../helper/journeyHelper';

const pdfToolbar = [
  {
    type: 'sidebar-thumbnails',
  },
  {
    type: 'sidebar-document-outline',
  },
  {
    type: 'sidebar-annotations',
  },
  {
    type: 'sidebar-bookmarks',
  },
  {
    type: 'pager',
  },
  {
    type: 'zoom-out',
  },
  {
    type: 'zoom-in',
  },
  {
    type: 'zoom-mode',
  },
  {
    type: 'spacer',
  },
  {
    type: 'annotate',
  },
  {
    type: 'text',
  },
  {
    type: 'note',
  },
  {
    type: 'signature',
  },
  {
    type: 'stamp',
  },
  {
    type: 'print',
  },
  {
    type: 'search',
  },
  {
    type: 'export-pdf',
  },
  {
    type: 'debug',
  },
];

function JourneyContentSignaturePdf({
  t,
  _csrf,
  step,
  pspdfkitLicenseKey,
  onRefreshJourney,
  onSkipStep,
  isFillAndSign,
}) {
  const {
    documents,
    _id,
    maxDays,
    title,
    description,
    taskStatus,
    userDocuments,
    formFields,
  } = step;
  const pdfViewerRef = useRef();
  const [showSubmitButton, setShowSubmitButton] = useState(
    !isFillAndSign ? true : taskStatus !== 'pending',
  );
  const [showRedoButton, setShowRedoButton] = useState(
    !isFillAndSign ? false : taskStatus === 'pending',
  );
  const { loggedUser } = useContext(LoggedUserContext);
  const [user, setUser] = useState(null);

  const pdfToRender = useMemo(() => {
    const originalPDF = documents && documents.length > 0 ? documents[0] : null;
    if (!isFillAndSign) {
      if (userDocuments && userDocuments.length > 0) {
        return userDocuments[0] || originalPDF;
      }
      return originalPDF;
    }
    if (!taskStatus || taskStatus === 'rejected') {
      return originalPDF;
    }
    return userDocuments[0] || originalPDF;
  }, []);
  const [pdf, setPdf] = useState(pdfToRender);

  /**
   * BRAVON-6332 Phase 2b — once the step templates (`journey-step-document`)
   * and the learner's submissions (`journey-step-user-document`) live on the
   * private bucket, their stored `url` is the authenticated api_media route.
   * The Quill/PSPDFKit viewers fetch it from the browser with no Bearer token,
   * so it has to be re-based onto this portal's session-gated `/file/...`
   * mirror first. Legacy public urls pass through untouched.
   */
  const pdfToView = useMemo(
    () => (pdf ? { ...pdf, url: resolveDocumentUrl(pdf.url) } : pdf),
    [pdf],
  );

  const [isLoading, setIsLoading] = useState(false);
  const handleIsLoading = isLoadingParam => setIsLoading(isLoadingParam);

  const handleSaveSignature = params => {
    handleIsLoading(true);
    axios
      .put(
        `/api/journey/step/${_id}/document/signed`,
        {
          documents: params,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('journey.step.pdf.upload.success'),
        );
        onRefreshJourney();
        handleIsLoading(false);
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
        console.log(err);
        handleIsLoading(false);
      });
  };

  const handleExportPDF = async () => {
    handleIsLoading(true);
    if (pdfViewerRef.current) {
      try {
        const { formFieldValues: values, formFields: fields } =
          await pdfViewerRef.current.exportFormFields();

        if (step.type === JOURNEY_STEP_TYPE.FILL_AND_SIGN) {
          const errors = [];
          const errorPromises = fields?.map(async field => {
            const { name, type, flags } = field;
            const fieldValue = values?.find(v => v.name === name);
            const isRequired = flags?.includes('required');
            const isSignature = type === 'pspdfkit/form-field/signature';

            if (isRequired) {
              if (!isSignature) {
                const hasNoValue =
                  !fieldValue?.value || fieldValue.value.length === 0;
                if (hasNoValue) {
                  return name;
                }
              } else {
                const signature =
                  await pdfViewerRef.current.checkForSignatureField(name);
                if (!signature) {
                  return name;
                }
              }
            }
            return null;
          });

          const results = await Promise.all(errorPromises);
          errors.push(...results.filter(name => name));
          if (errors.length > 0) {
            Notifications.showNotificationError(
              t('error'),
              t(
                'fill.and.sign.error.required',
                errors.length,
                errors.join(', '),
              ),
            );
            handleIsLoading(false);
            return;
          }
        }

        const data = await pdfViewerRef.current.exportPDF();
        const response = await handleUploadFile(_csrf, null, {
          ...data,
          originalName: pdf.name,
        });
        await handleSaveSignature([response]);
      } catch (err) {
        Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
        console.log('*ERR', err);
        handleIsLoading(false);
      }
    }
  };

  const handleRedoPDF = () => {
    setShowSubmitButton(true);
    setShowRedoButton(false);
    setPdf(documents && documents.length > 0 ? documents[0] : null);
  };

  const renderTheFormFields =
    isFillAndSign &&
    formFields &&
    (!taskStatus ||
      taskStatus === 'rejected' ||
      (taskStatus === 'pending' && !showRedoButton));

  const getUser = async () => {
    const { data: response } = await axios('/api/user/me', {
      headers: {
        'Accept-Version': '1.0.0',
        'X-Gamification-API': 'Organization',
        'X-CSRF-Token': _csrf,
        _csrf,
      },
    });

    setUser(response.data);
  };

  useEffect(() => {
    getUser();
  }, []);

  const toolbar = useMemo(() => {
    if (taskStatus === 'approved') {
      return [
        {
          type: 'sidebar-thumbnails',
        },
        {
          type: 'sidebar-document-outline',
        },
        {
          type: 'sidebar-annotations',
        },
        {
          type: 'sidebar-bookmarks',
        },
        {
          type: 'pager',
        },
        {
          type: 'zoom-out',
        },
        {
          type: 'zoom-in',
        },
        {
          type: 'zoom-mode',
        },
        {
          type: 'spacer',
        },
        {
          type: 'print',
        },
        {
          type: 'search',
        },
        {
          type: 'export-pdf',
        },
        {
          type: 'debug',
        },
      ];
    }
    return pdfToolbar;
  }, [taskStatus, pdfToolbar]);
  const PdfComponent = QuillPdf;
  // Signature steps use the free-form "sign anywhere" tool; fill_and_sign keeps
  // the field-based filler above.
  const SignComponent = QuillSigner;
  return (
    pdf && (
      <div id="signPdfContainer" className="row">
        <div className="col-lg-4 col-12">
          <PdfInfoSection
            title={title}
            description={description}
            maxDays={maxDays}
            onUploadPdf={handleSaveSignature}
            onLoading={handleIsLoading}
            isLoading={isLoading}
            status={taskStatus}
            customOnClick={handleExportPDF}
            isFillAndSign={isFillAndSign}
            onRedoPDF={handleRedoPDF}
            showSubmitButton={showSubmitButton}
            showRedoButton={showRedoButton}
          />
        </div>
        <div className="col-lg-8 col-12">
          <div className="m-portlet m-portlet-rounded">
            <div className="m-portlet__body">
              {/**
               For fill and sign since there are some user information that we need to get in real time,
               we need to wait until the request of user/me is finished before loading the pdf, since after the load we cannot
               manage it again
               */}
              {isFillAndSign ? (
                <>
                  {user ? (
                    <PdfComponent
                      t={t}
                      ref={pdfViewerRef}
                      documentFile={pdfToView}
                      id="signaturePdfViewer"
                      onReadingFinished={() => {}}
                      pspdfkitLicenseKey={pspdfkitLicenseKey}
                      pdfToolbar={toolbar}
                      originalName={pdf.originalName}
                      onSkipStep={onSkipStep}
                      formFields={
                        renderTheFormFields
                          ? fillFormFields(formFields, user)
                          : null
                      }
                    />
                  ) : null}
                </>
              ) : (
                <SignComponent
                  t={t}
                  ref={pdfViewerRef}
                  documentFile={pdfToView}
                  id="signaturePdfViewer"
                  onReadingFinished={() => {}}
                  pspdfkitLicenseKey={pspdfkitLicenseKey}
                  pdfToolbar={toolbar}
                  originalName={pdf.originalName}
                  onSkipStep={onSkipStep}
                  formFields={
                    renderTheFormFields
                      ? fillFormFields(formFields, loggedUser)
                      : null
                  }
                />
              )}
              {(showSubmitButton && taskStatus !== 'approved') ||
              showRedoButton ? (
                <div className="pdf-action-buttons">
                  {showSubmitButton && taskStatus !== 'approved' && (
                    <FileUploadBtn
                      label={
                        isFillAndSign
                          ? t('journey.step.fill.upload')
                          : t('journey.step.pdf.title')
                      }
                      onLoading={handleIsLoading}
                      handleFile={handleSaveSignature}
                      isLoading={isLoading}
                      customOnClick={handleExportPDF}
                      _csrf={_csrf}
                    />
                  )}
                  {showRedoButton ? (
                    <button
                      type="button"
                      onClick={handleRedoPDF}
                      className="btn btn-dynamic btn-sm m-btn--pill btn-file redo-button"
                    >
                      {t('redo.pdf')}
                      <i className="fa fa-file-signature" />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  );
}

JourneyContentSignaturePdf.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  step: PropTypes.shape(JourneyStepProp.propType).isRequired,
  pspdfkitLicenseKey: PropTypes.string,
  onRefreshJourney: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
  isFillAndSign: PropTypes.bool,
};

JourneyContentSignaturePdf.defaultProps = {
  pspdfkitLicenseKey: null,
  onSkipStep: null,
  isFillAndSign: false,
};

export default compose(translate, withCSRF)(JourneyContentSignaturePdf);
